import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Send, ArrowLeft, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Navbar } from '@/components/Navbar';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';

const MessagesPage = () => {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Check auth on mount
  useEffect(() => {
    const headers = getAuthHeaders();
    axios.get(`${API}/auth/me`, { withCredentials: true, headers })
      .then(res => {
        setUser(res.data);
      })
      .catch(() => {
        navigate('/auth');
      });
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchConversations();
      // Poll for new messages every 5 seconds
      pollIntervalRef.current = setInterval(fetchConversations, 5000);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [user]);

  useEffect(() => {
    if (conversationId) {
      fetchMessages(conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/messages`, { withCredentials: true, headers });
      setConversations(res.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      // Surface auth/network errors to the user instead of silently showing "no conversations"
      if (error.response?.status === 401) {
        toast.error('Please sign in again to see your messages.');
        navigate('/auth');
      } else if (!error.response) {
        toast.error('Network issue — could not load messages. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (convId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/messages/${convId}`, { withCredentials: true, headers });
      setMessages(res.data.messages || []);
      setActiveConversation(res.data.conversation);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    const recipientId = activeConversation.participants.find(p => p !== user.user_id);
    
    setSending(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/messages`, {
        recipient_id: recipientId,
        booking_id: activeConversation.booking_id,
        message: newMessage.trim()
      }, { withCredentials: true, headers });
      
      setNewMessage('');
      fetchMessages(activeConversation.conversation_id);
      fetchConversations();
    } catch (error) {
      console.error('Send message error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please sign in again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send message. Try again.');
      }
    } finally {
      setSending(false);
    }
  };

  const selectConversation = (conv) => {
    navigate(`/messages/${conv.conversation_id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5ebc67]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Messages</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[600px]">
          {/* Conversations List */}
          <Card className="md:col-span-1 overflow-hidden">
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {conversations.length === 0 ? (
                  <div className="p-8 text-center">
                    <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No messages yet</p>
                    <p className="text-sm text-slate-400">Start a conversation after booking</p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <button
                      key={conv.conversation_id}
                      onClick={() => selectConversation(conv)}
                      className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${
                        activeConversation?.conversation_id === conv.conversation_id ? 'bg-slate-100' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#5ebc67] rounded-full flex items-center justify-center text-white font-bold">
                          {conv.other_user?.name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-slate-900 truncate">
                              {conv.other_user?.name || 'User'}
                            </p>
                            {conv.unread_count > 0 && (
                              <span className="bg-[#5ebc67] text-white text-xs px-2 py-0.5 rounded-full">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 truncate">
                            {conv.last_message?.message || 'No messages'}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Messages */}
          <Card className="md:col-span-2 flex flex-col overflow-hidden">
            {activeConversation ? (
              <>
                {/* Header */}
                <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                  <Button variant="ghost" size="icon" className="md:hidden" onClick={() => navigate('/messages')}>
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div className="w-10 h-10 bg-[#5ebc67] rounded-full flex items-center justify-center text-white font-bold">
                    {conversations.find(c => c.conversation_id === activeConversation.conversation_id)?.other_user?.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">
                      {conversations.find(c => c.conversation_id === activeConversation.conversation_id)?.other_user?.name || 'User'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {activeConversation.booking_id ? 'Re: Booking' : 'Direct message'}
                    </p>
                  </div>
                </div>

                {/* Messages List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg) => {
                    const isOwn = msg.sender_id === user.user_id;
                    return (
                      <div
                        key={msg.message_id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                            isOwn
                              ? 'bg-[#5ebc67] text-white rounded-br-md'
                              : 'bg-slate-100 text-slate-900 rounded-bl-md'
                          }`}
                        >
                          <p>{msg.message}</p>
                          <p className={`text-xs mt-1 ${isOwn ? 'text-green-100' : 'text-slate-400'}`}>
                            {format(new Date(msg.created_at), 'h:mm a')}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={sendMessage} className="p-4 border-t border-slate-100">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      disabled={sending}
                      className="flex-1"
                      data-testid="message-input"
                    />
                    <Button 
                      type="submit" 
                      disabled={sending || !newMessage.trim()}
                      className="bg-[#5ebc67] hover:bg-[#4da855]"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Select a conversation</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
